import { injectStyle, createPageShell } from "./style.js"

interface TreeNode {
    name: string
    path: string
    children: Map<string, TreeNode>
}

function buildTree(filePaths: string[], stripPrefix: string): TreeNode {
    const root: TreeNode = { name: "", path: "", children: new Map() }
    for (const filePath of filePaths) {
        const relative = filePath.startsWith(stripPrefix)
            ? filePath.slice(stripPrefix.length)
            : filePath
        const parts = relative.split("/")
        let current = root
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i]
            if (!current.children.has(part)) {
                current.children.set(part, {
                    name: part,
                    path: parts.slice(0, i + 1).join("/"),
                    children: new Map(),
                })
            }
            const next = current.children.get(part)
            if (!next) break
            current = next
        }
    }
    return root
}

function renderTreeNode(node: TreeNode, baseUrl: string): HTMLElement {
    const li = document.createElement("li")

    if (node.children.size > 0) {
        // Directory
        const details = document.createElement("details")
        details.open = true
        const summary = document.createElement("summary")
        summary.textContent = node.name + "/"
        details.appendChild(summary)

        const ul = document.createElement("ul")
        ul.style.listStyle = "none"
        ul.style.paddingLeft = "20px"
        const sorted = Array.from(node.children.values()).sort((a, b) => {
            // Directories first, then files.
            const aIsDir = a.children.size > 0
            const bIsDir = b.children.size > 0
            if (aIsDir !== bIsDir) return aIsDir ? -1 : 1
            return a.name.localeCompare(b.name)
        })
        for (const child of sorted) {
            ul.appendChild(renderTreeNode(child, baseUrl))
        }
        details.appendChild(ul)
        li.appendChild(details)
    } else {
        // File
        const a = document.createElement("a")
        a.href = baseUrl + node.path
        a.textContent = node.name
        li.appendChild(a)
    }

    return li
}

const css = `
.file-tree {
    padding: 16px 24px;
    line-height: 1.8;
}

.file-tree a {
    color: light-dark(#0969da, #58a6ff);
    text-decoration: none;
}

.file-tree a:hover {
    text-decoration: underline;
}

.file-tree summary {
    cursor: pointer;
    font-weight: 600;
}

.file-tree ul {
    margin: 0;
}
`


interface FileTreeOptions {
    filePaths: string[]
    stripPrefix: string
    baseUrl: string
    header?: HTMLElement
}

export function createFileTree({ filePaths, stripPrefix, baseUrl, header }: FileTreeOptions): HTMLElement {
    injectStyle("file-tree", css)

    const { container, content } = createPageShell({ header })

    const tree = document.createElement("div")
    tree.className = "file-tree"

    const root = buildTree(filePaths, stripPrefix)

    const ul = document.createElement("ul")
    ul.style.listStyle = "none"
    ul.style.padding = "0"
    const sorted = Array.from(root.children.values()).sort((a, b) => {
        const aIsDir = a.children.size > 0
        const bIsDir = b.children.size > 0
        if (aIsDir !== bIsDir) return aIsDir ? -1 : 1
        return a.name.localeCompare(b.name)
    })
    for (const child of sorted) {
        ul.appendChild(renderTreeNode(child, baseUrl))
    }
    tree.appendChild(ul)
    content.appendChild(tree)

    return container
}
